import { useCallback, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, Navigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Pencil, Trash2, Check, X, Filter, 
  BarChart3, Clock, Zap, Code, Palette, Server, Layout,
  ChevronDown, Search, RefreshCw, Database, Loader2,
  CheckCircle2, Circle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  api: Code,
  feature: Zap,
  backend: Server,
  frontend: Layout,
  design: Palette,
  infra: Database
};

const categoryColors = {
  api: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  feature: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  backend: 'bg-green-500/20 text-green-400 border-green-500/30',
  frontend: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  design: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  infra: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const priorityColors = {
  P0: 'bg-red-500/20 text-red-400 border-red-500/30',
  P1: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  P2: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

const statusIcons = {
  todo: Circle,
  in_progress: Clock,
  done: CheckCircle2
};

export const AdminPage = () => {
  const { isDark } = useTheme();
  const { token, isAdmin, loading: authLoading } = useAuth();
  
  const [roadmap, setRoadmap] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ priority: 'all', category: 'all', status: 'all' });
  const [search, setSearch] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'feature',
    priority: 'P1',
    status: 'todo',
    estimated_effort: '',
    tags: []
  });

  const fetchRoadmap = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/roadmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoadmap(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la roadmap');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && isAdmin) {
      fetchRoadmap();
    }
  }, [token, isAdmin, fetchRoadmap]);

  const seedRoadmap = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/roadmap/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchRoadmap();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const handleSubmit = async () => {
    try {
      if (editItem) {
        await axios.patch(`${API_URL}/api/admin/roadmap/${editItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item mis à jour');
      } else {
        await axios.post(`${API_URL}/api/admin/roadmap`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item créé');
      }
      setIsDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchRoadmap();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet item ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/roadmap/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item supprimé');
      fetchRoadmap();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/admin/roadmap/${item.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRoadmap();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category,
      priority: item.priority,
      status: item.status,
      estimated_effort: item.estimated_effort || '',
      tags: item.tags || []
    });
    setIsDialogOpen(true);
  };

  const openCreate = () => {
    setEditItem(null);
    resetForm();
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'feature',
      priority: 'P1',
      status: 'todo',
      estimated_effort: '',
      tags: []
    });
  };

  // Filter items
  const filteredItems = roadmap.items?.filter(item => {
    if (filter.priority !== 'all' && item.priority !== filter.priority) return false;
    if (filter.category !== 'all' && item.category !== filter.category) return false;
    if (filter.status !== 'all' && item.status !== filter.status) return false;
    if (search && !item.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }) || [];

  // Group by priority
  const groupedItems = {
    P0: filteredItems.filter(i => i.priority === 'P0'),
    P1: filteredItems.filter(i => i.priority === 'P1'),
    P2: filteredItems.filter(i => i.priority === 'P2')
  };

  if (authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#81d8d0]" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className={`min-h-screen px-4 py-6 transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link 
              to="/dashboard" 
              className={`inline-flex items-center gap-2 mb-4 transition-colors ${
                isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
              }`}
            >
              <ArrowLeft size={18} />
              <span>Retour au dashboard</span>
            </Link>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Admin — Roadmap
            </h1>
            <p className={`mt-1 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
              Gestion des fonctionnalités et APIs à implémenter
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={seedRoadmap}
              variant="outline"
              className={`${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200'}`}
            >
              <Database size={16} className="mr-2" />
              Initialiser
            </Button>
            <Button
              onClick={fetchRoadmap}
              variant="outline"
              className={`${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200'}`}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin mr-2' : 'mr-2'} />
              Actualiser
            </Button>
            <Button
              onClick={openCreate}
              className="bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A]"
            >
              <Plus size={16} className="mr-2" />
              Ajouter
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#81d8d0]/20 flex items-center justify-center">
                <BarChart3 className="text-[#81d8d0]" size={20} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {roadmap.stats?.total || 0}
                </p>
                <p className={`text-xs ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Total</p>
              </div>
            </div>
          </Card>
          
          <Card className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="text-red-400" size={20} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {roadmap.stats?.by_priority?.P0 || 0}
                </p>
                <p className={`text-xs ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>P0 Critique</p>
              </div>
            </div>
          </Card>
          
          <Card className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="text-yellow-400" size={20} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {roadmap.stats?.by_status?.in_progress || 0}
                </p>
                <p className={`text-xs ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>En cours</p>
              </div>
            </div>
          </Card>
          
          <Card className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="text-green-400" size={20} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {roadmap.stats?.by_status?.done || 0}
                </p>
                <p className={`text-xs ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Terminé</p>
              </div>
            </div>
          </Card>
          
          <Card className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#81d8d0]/20 flex items-center justify-center">
                <Zap className="text-[#81d8d0]" size={20} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {roadmap.stats?.progress_percent || 0}%
                </p>
                <p className={`text-xs ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Progression</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className={`p-4 mb-8 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Progression globale</span>
            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {roadmap.stats?.by_status?.done || 0} / {roadmap.stats?.total || 0}
            </span>
          </div>
          <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
            <motion.div 
              className="h-full bg-gradient-to-r from-[#81d8d0] to-[#4ECDC4]"
              initial={{ width: 0 }}
              animate={{ width: `${roadmap.stats?.progress_percent || 0}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </Card>

        {/* Filters */}
        <Card className={`p-4 mb-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`} size={18} />
                <Input
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`pl-10 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                />
              </div>
            </div>
            
            <Select value={filter.priority} onValueChange={(v) => setFilter({...filter, priority: v})}>
              <SelectTrigger className={`w-[130px] ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="P0">P0 - Critique</SelectItem>
                <SelectItem value="P1">P1 - Important</SelectItem>
                <SelectItem value="P2">P2 - Backlog</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filter.category} onValueChange={(v) => setFilter({...filter, category: v})}>
              <SelectTrigger className={`w-[130px] ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="api">API</SelectItem>
                <SelectItem value="feature">Feature</SelectItem>
                <SelectItem value="backend">Backend</SelectItem>
                <SelectItem value="frontend">Frontend</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="infra">Infra</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filter.status} onValueChange={(v) => setFilter({...filter, status: v})}>
              <SelectTrigger className={`w-[130px] ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="todo">À faire</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="done">Terminé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Items List by Priority */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#81d8d0]" />
          </div>
        ) : (
          <div className="space-y-8">
            {['P0', 'P1', 'P2'].map(priority => (
              groupedItems[priority].length > 0 && (
                <div key={priority}>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <span className={`px-2 py-0.5 rounded text-xs ${priorityColors[priority]}`}>{priority}</span>
                    {priority === 'P0' && 'Critique'}
                    {priority === 'P1' && 'Important'}
                    {priority === 'P2' && 'Backlog'}
                    <span className={`text-sm font-normal ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>
                      ({groupedItems[priority].length})
                    </span>
                  </h3>
                  
                  <div className="space-y-3">
                    {groupedItems[priority].map((item, index) => {
                      const CategoryIcon = categoryIcons[item.category] || Zap;
                      const StatusIcon = statusIcons[item.status] || Circle;
                      
                      return (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card className={`p-4 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/8' : 'bg-white border-gray-200 hover:bg-gray-50'} transition-colors`}>
                            <div className="flex items-start gap-4">
                              {/* Status toggle */}
                              <button
                                onClick={() => handleStatusChange(item, item.status === 'done' ? 'todo' : item.status === 'todo' ? 'in_progress' : 'done')}
                                className={`mt-1 transition-colors ${
                                  item.status === 'done' ? 'text-green-400' : 
                                  item.status === 'in_progress' ? 'text-yellow-400' : 
                                  isDark ? 'text-[#64748B] hover:text-[#94A3B8]' : 'text-gray-400 hover:text-gray-600'
                                }`}
                              >
                                <StatusIcon size={20} />
                              </button>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <h4 className={`font-medium ${item.status === 'done' ? 'line-through opacity-60' : ''} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {item.title}
                                  </h4>
                                  <span className={`px-2 py-0.5 rounded text-xs border ${categoryColors[item.category]}`}>
                                    {item.category}
                                  </span>
                                </div>
                                {item.description && (
                                  <p className={`text-sm mb-2 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                                    {item.description}
                                  </p>
                                )}
                                {item.tags?.length > 0 && (
                                  <div className="flex gap-1 flex-wrap">
                                    {item.tags.map(tag => (
                                      <span key={tag} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/5 text-[#64748B]' : 'bg-gray-100 text-gray-500'}`}>
                                        #{tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openEdit(item)}
                                  className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-[#94A3B8]' : 'hover:bg-gray-100 text-gray-500'}`}
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )
            ))}
            
            {filteredItems.length === 0 && (
              <div className={`text-center py-12 ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>
                {roadmap.items?.length === 0 
                  ? "Aucun item. Cliquez sur 'Initialiser' pour créer la roadmap par défaut."
                  : "Aucun résultat pour ces filtres."
                }
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className={`${isDark ? 'bg-[#1a1a2e] border-white/10' : 'bg-white'} max-w-lg`}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
                {editItem ? 'Modifier l\'item' : 'Nouvel item'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  placeholder="Ex: Intégration Stripe"
                />
              </div>
              
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className={`mt-1 w-full px-3 py-2 rounded-md text-sm ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-200'}`}
                  placeholder="Description détaillée..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Catégorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="feature">Feature</SelectItem>
                      <SelectItem value="backend">Backend</SelectItem>
                      <SelectItem value="frontend">Frontend</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="infra">Infra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Priorité</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                    <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P0">P0 - Critique</SelectItem>
                      <SelectItem value="P1">P1 - Important</SelectItem>
                      <SelectItem value="P2">P2 - Backlog</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Statut</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">À faire</SelectItem>
                      <SelectItem value="in_progress">En cours</SelectItem>
                      <SelectItem value="done">Terminé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Effort estimé</Label>
                  <Input
                    value={formData.estimated_effort}
                    onChange={(e) => setFormData({...formData, estimated_effort: e.target.value})}
                    className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                    placeholder="Ex: 2h, 1d, 1w"
                  />
                </div>
              </div>
              
              <div>
                <Label className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>Tags (séparés par des virgules)</Label>
                <Input
                  value={formData.tags?.join(', ') || ''}
                  onChange={(e) => setFormData({...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})}
                  className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200'}`}
                  placeholder="api, stripe, paiement"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} className={isDark ? 'border-white/10' : ''}>
                Annuler
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!formData.title}
                className="bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A]"
              >
                {editItem ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
